import { useRef, useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';

import useSection from '@/services/useSection';
import FastUsePlayer from './FastUsePlayer/FastUsePlayer';
import Slider from '@/components/Slider';
import FastUseSliderIndicator from './FastUseSliderIndicator';

const FastUseContainer = ({ data, isInView }) => {
    const [selectedContent, setSelectedContent] = useState(null);
    const [sliderResults, setSliderResults] = useState([]);
    const [muted, setMuted] = useState(true);

    const { id } = data;

    const { sectionMetaData, sectionData, sectionDataFetchNextPage } = useSection({
        id,
        fetchSections: isInView
    });

    const { ref: endOfIndicatorListRef, inView: endOflistInView } = useInView({
        threshold: 0.1
    });

    const generateSlidesList = () => {
        let allData = [];
        sectionData?.pages?.forEach((resultsPage) => {
            if (resultsPage) {
                allData = [...allData, ...resultsPage.results];
            }
        });

        setSliderResults(allData);
    };

    const slidersListContainer = useRef(null);
    const sliderRef = useRef(null);
    const sliderIndicatorRef = useRef([]);
    const allDataCountRef = useRef(null);

    useEffect(() => {
        setSelectedContent(0);
        setSliderResults(data?.cards);

        const handleSliderEndReach = () => {
            sectionDataFetchNextPage();
        };
        const handleSideNextTransitionEnd = (swiper, e) => {
            if (swiper.activeIndex === allDataCountRef?.current - 1 && e.clientX - e.offsetX > 70) {
                setTimeout(() => {
                    goToSlide(0);
                }, 500);
            }
        };

        if (sliderRef.current) {
            sliderRef.current.onEndReach(handleSliderEndReach);
            sliderRef.current.onSliderMove(handleSideNextTransitionEnd);
        }

        return () => {
            sliderRef?.current?.offAnyEvents();
        };
    }, []);

    useEffect(() => {
        allDataCountRef.current = sectionMetaData?.card_count;
        generateSlidesList();
        setTimeout(() => {
            sliderRef?.current?.swiperUpdate();
        }, 1000);
    }, [sectionData, sectionMetaData]);

    const scrollToSelectedSlide = (index) => {
        if (sliderIndicatorRef.current[index] && isInView) {
            sliderIndicatorRef.current[index].scrollIntoView({
                behavior: 'smooth',
                block: 'nearest'
            });
        }
    };

    const goToSlide = (index) => {
        sliderRef.current.setSlideIndex(index);
        setSelectedContent(index);
    };

    const goToNextSlide = (index) => {
        if (index === sliderResults.length - 1) {
            sectionDataFetchNextPage().then((res) => {
                sliderRef.current.setSlideIndex(selectedContent + 1);
                setSelectedContent(selectedContent + 1);
                if (index === allDataCountRef.current - 1 && !res?.hasNextPage) {
                    goToSlide(0);
                }
            });
        } else {
            sliderRef.current.goToNextSlide();
            setSelectedContent(selectedContent + 1);
        }
    };

    useEffect(() => {
        scrollToSelectedSlide(selectedContent);
    }, [selectedContent]);

    useEffect(() => {
        sectionDataFetchNextPage();
    }, [endOflistInView]);

    const handleSlideChange = (selectedSlideIndex) => {
        setSelectedContent(selectedSlideIndex);
    };

    return (
        <div className="fast-use-container">
            <Slider
                ref={sliderRef}
                className="fast-use-slider"
                initialSlide={0}
                handleSlideChange={handleSlideChange}
                swiperContainerProps={{
                    'slides-per-view': 1,
                    'centered-slides': true,
                    'space-between': 16,
                    navigation: false,
                    pagination: false,
                    loop: false
                }}
                slides={sliderResults.map((item, index) => {
                    return (
                        <FastUsePlayer
                            key={item.id}
                            data={item}
                            handleVideoOnEnd={() => {
                                goToNextSlide(index);
                            }}
                            selectedContent={sliderResults?.[selectedContent]}
                            isInView={isInView}
                            muted={muted}
                            setMuted={setMuted}
                        />
                    );
                })}
            />

            <div className="fast-use-slider-container hide-scroll" ref={slidersListContainer}>
                <FastUseSliderIndicator
                    data={sliderResults}
                    ref={sliderIndicatorRef}
                    selectedContent={selectedContent}
                    handleClick={goToSlide}
                />
                <div ref={endOfIndicatorListRef} className="fast-use-slider-end-of-list" />
            </div>
        </div>
    );
};

export default FastUseContainer;
